import { useState, useRef } from 'react';
import { C } from '../styles/tokens.js';

// 헤더에 통합되는 멀티 탭 프로젝트 스위처
// - 활성 프로젝트 + 다른 프로젝트들이 탭 형식으로 나열
// - 4개 초과 시 ⋯ 접힘
// - 우측 ⋮ 메뉴: 검색·이름변경·복제·삭제·백업·복원
const MAX_VISIBLE = 4;

export default function ProjectTabs({
  projects, current, setCurrentId,
  createProject, duplicateProject, renameProject, deleteProject,
  exportBackup, importBackup, search,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [renaming, setRenaming] = useState(null);
  const [renameText, setRenameText] = useState('');
  const fileRef = useRef(null);
  const [toast, setToast] = useState(null);
  const showToast = (m) => { setToast(m); setTimeout(() => setToast(null), 2500); };

  // 표시: 활성 프로젝트가 항상 보이도록 정렬
  const sorted = [...projects].sort((a, b) => {
    if (a.id === current?.id) return -1;
    if (b.id === current?.id) return 1;
    return new Date(b.updatedAt) - new Date(a.updatedAt);
  });
  const visible = sorted.slice(0, MAX_VISIBLE);
  const hidden = sorted.slice(MAX_VISIBLE);
  const canAddInline = visible.length < MAX_VISIBLE;

  const onImport = async (file) => {
    const r = await importBackup(file);
    showToast(r.ok ? `${r.count}개 프로젝트 가져옴` : `실패: ${r.msg}`);
  };

  const onCreate = () => {
    const name = window.prompt('새 제안 프로젝트 이름:', '');
    if (name && name.trim()) {
      createProject(name.trim());
      showToast('새 프로젝트 생성');
    }
  };

  const filtered = query ? search(query) : projects;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, position: 'relative' }}>
      {/* 탭들 */}
      {visible.map(p => {
        const active = p.id === current?.id;
        const isRenaming = renaming === p.id;
        return (
          <div key={p.id}
            onClick={() => { if (!isRenaming) setCurrentId(p.id); }}
            onDoubleClick={() => { if (active) { setRenameText(p.name); setRenaming(p.id); } }}
            title={active ? '더블클릭으로 이름 변경' : '클릭하여 전환'}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '6px 12px', borderRadius: 8, cursor: 'pointer', transition: 'all 0.15s',
              background: active ? C.goldBg : 'transparent',
              border: `1px solid ${active ? C.gold : C.border}`,
              boxShadow: active ? `0 0 0 1px ${C.gold}33` : 'none',
              color: active ? C.gold : C.dim, fontSize: 12, fontWeight: 700,
              maxWidth: 200, minWidth: 0,
            }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: active ? C.gold : C.dim2, flexShrink: 0, boxShadow: active ? `0 0 6px ${C.gold}` : 'none' }} />
            {isRenaming ? (
              <input value={renameText} onChange={e => setRenameText(e.target.value)} autoFocus
                onClick={e => e.stopPropagation()}
                onBlur={() => { if (renameText.trim()) renameProject(p.id, renameText.trim()); setRenaming(null); }}
                onKeyDown={e => {
                  if (e.key === 'Enter') { if (renameText.trim()) renameProject(p.id, renameText.trim()); setRenaming(null); }
                  if (e.key === 'Escape') setRenaming(null);
                }}
                style={{ width: 160, padding: '2px 6px', background: C.surface3, border: `1px solid ${C.gold}`, borderRadius: 4, color: C.text, fontSize: 12, outline: 'none' }} />
            ) : (
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
            )}
            {active && !isRenaming && (
              <button onClick={(e) => {
                  e.stopPropagation();
                  if (projects.length === 1) { showToast('마지막 프로젝트는 삭제할 수 없습니다'); return; }
                  if (window.confirm(`'${p.name}' 프로젝트를 삭제할까요?`)) deleteProject(p.id);
                }}
                title="이 프로젝트 삭제"
                style={{ background: 'transparent', border: 'none', color: active ? C.gold : C.dim, cursor: 'pointer', padding: 0, fontSize: 12, opacity: 0.6 }}>×</button>
            )}
          </div>
        );
      })}

      {/* 숨겨진 프로젝트 ⋯ */}
      {hidden.length > 0 && (
        <button onClick={() => setMenuOpen(true)}
          title={`${hidden.length}개 더`}
          style={{ padding: '5px 10px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface2, color: C.dim, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
          ⋯ +{hidden.length}
        </button>
      )}

      {/* + 신규 */}
      {canAddInline && (
        <button onClick={onCreate}
          title="새 제안 프로젝트"
          style={{ padding: '5px 10px', borderRadius: 6, border: `1px dashed ${C.gold}66`, background: 'transparent', color: C.gold, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>+</button>
      )}

      {/* ⋮ 메뉴 */}
      <button onClick={() => setMenuOpen(o => !o)}
        title="프로젝트 메뉴"
        style={{ padding: '5px 8px', borderRadius: 6, border: `1px solid ${C.border}`, background: menuOpen ? C.goldBg : 'transparent', color: C.dim, fontSize: 14, cursor: 'pointer', marginLeft: 4 }}>⋮</button>

      {/* 메뉴 패널 */}
      {menuOpen && (
        <>
          <div onClick={() => setMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 100 }} />
          <div style={{
            position: 'absolute', top: 'calc(100% + 6px)', right: 0, minWidth: 360, maxWidth: 440,
            background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10,
            boxShadow: '0 12px 40px rgba(0,0,0,0.6)', zIndex: 101, padding: 12,
          }}>
            <input value={query} onChange={e => setQuery(e.target.value)}
              placeholder="🔍 프로젝트 검색 (이름·카드·마감일)"
              style={{ width: '100%', padding: '8px 12px', background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 12, outline: 'none', marginBottom: 10 }} />

            <div style={{ maxHeight: 280, overflowY: 'auto', marginBottom: 10 }}>
              {filtered.length === 0 ? (
                <div style={{ padding: '20px 10px', color: C.dim, fontSize: 12, textAlign: 'center' }}>검색 결과 없음</div>
              ) : filtered.map(p => {
                const isCurrent = p.id === current?.id;
                return (
                  <div key={p.id}
                    onClick={() => { setCurrentId(p.id); setMenuOpen(false); }}
                    style={{ padding: '10px 12px', background: isCurrent ? C.goldBg : 'transparent', borderRadius: 6, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: isCurrent ? C.gold : C.text, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                      <div style={{ fontSize: 10, color: C.dim, marginTop: 2 }}>
                        카드 {(p.data?.cards || []).length} · 마감일 {(p.data?.dates || []).length} · {new Date(p.updatedAt).toLocaleDateString('ko-KR')}
                      </div>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); duplicateProject(p.id); showToast('복제됨'); }}
                      title="복제" style={{ background: 'transparent', border: 'none', color: C.dim, cursor: 'pointer', fontSize: 13 }}>⎘</button>
                    <button onClick={(e) => { e.stopPropagation(); exportBackup(p.id); showToast('JSON 다운로드'); }}
                      title="이 프로젝트만 백업" style={{ background: 'transparent', border: 'none', color: C.dim, cursor: 'pointer', fontSize: 13 }}>⬇</button>
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', gap: 6, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
              <button onClick={() => { onCreate(); setMenuOpen(false); }}
                style={{ flex: 1, padding: '7px 10px', borderRadius: 6, border: `1px dashed ${C.gold}66`, background: C.goldBg, color: C.gold, fontSize: 11, cursor: 'pointer', fontWeight: 700 }}>+ 새 프로젝트</button>
              <button onClick={() => { exportBackup(); showToast('전체 백업'); }}
                style={{ flex: 1, padding: '7px 10px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface2, color: C.text, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>⬇ 전체 백업</button>
              <label style={{ flex: 1, padding: '7px 10px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface2, color: C.text, fontSize: 11, cursor: 'pointer', fontWeight: 600, textAlign: 'center' }}>
                ⬆ 불러오기
                <input ref={fileRef} type="file" accept=".json" style={{ display: 'none' }}
                  onChange={(e) => { onImport(e.target.files?.[0]); if (fileRef.current) fileRef.current.value = ''; }} />
              </label>
            </div>

            <div style={{ marginTop: 8, fontSize: 10, color: C.dim, textAlign: 'center' }}>
              총 {projects.length}개 · localStorage 자동 저장 · 활성 탭 더블클릭으로 이름 변경
            </div>
          </div>
        </>
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: 32, left: '50%', transform: 'translateX(-50%)',
          background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 8,
          padding: '12px 20px', color: C.text, fontSize: 13, fontWeight: 600,
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)', zIndex: 9999,
        }}>{toast}</div>
      )}
    </div>
  );
}
